import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
// NotificationsProvider is mounted in index.js to scope the entire app
import LiveRegion from './components/LiveRegion';
import Dashboard from './components/Dashboard';
import Clients from './components/Clients';
import Suppliers from './components/Suppliers';
import Brokers from './components/Brokers';
import Branches from './components/Branches';
import Businesses from './components/Businesses';
import Transactions from './components/Transactions';
import Reports from './components/Reports';
import PurchaseManagement from './components/PurchaseManagement';
import PendingCashCalendar from './components/PendingCashCalendar';
// Removed dependency on mocks; use a sane default for own-funds limit.
const OWN_FUNDS_LIMIT = 10_000_000;
import { useNotifications } from './components/Notifications';
import { useData } from './providers/DataProvider';
import CommandPalette from './components/ui/CommandPalette';
import ThemeToggle from './components/ThemeToggle';
import BotNotifications from './components/BotNotifications';
import SettingsPage from './components/SettingsPage';
import LoadingIndicator from './components/ui/LoadingIndicator';
import { Settings, LogOut } from 'lucide-react';
import { supabase } from './lib/supabaseClient';
import { appLogout } from './lib/logout';
import Profits from './components/Profits';
import StatusPage from './components/StatusPage';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import BentoGrid from './components/ui/BentoGrid';

function App() {
  const { push } = useNotifications ? useNotifications() : { push: () => {} };
  const {
    loading,
    useDb,
  currentUser, setCurrentUser,
  hasSession,
  branches,
    clients, setClients,
    suppliers, setSuppliers,
    brokers, setBrokers,
    transactions, setTransactions,
    dbAddTransaction,
    dbUpdateTransaction,
    dbDeleteTransaction,
    dbAddClient,
    dbUpdateClient,
    dbDeleteClient,
    dbAddSupplier,
    dbUpdateSupplier,
    dbDeleteSupplier,
    dbAddBroker,
  dbUpdateBroker,
  dbAddBranch,
  dbUpdateBranch,
  dbAddDeposit,
  dbDeleteDeposit,
  dbListDeposits,
  uploadDepositProof,
    dbCancelTransaction,
    dbDeliver,
    dbMarkDelivered,
    dbMarkBrokerCommissionPaid,
  dbAddBranchCashMovement,
  dbTransferBranchCash,
    error: dataError,
    preferences, savePreferences,
  } = useData();
  // Settings page is now a route; modal removed
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  if (loading && !hasSession) {
    return (
      <Router>
        <div className="min-h-screen bg-gray-100 dark:bg-gray-950 flex flex-col items-center justify-center relative overflow-hidden">
          <LoadingIndicator label="Cargando información actualizada..." />
        </div>
      </Router>
    );
  }

  // Simple auth gate: in DB mode require session; else allow mock
  if (!hasSession) {
    return (
      <Router>
        <div className="min-h-screen bg-gray-100 dark:bg-gray-950 relative overflow-hidden">
          <BentoGrid variant="subtle" className="absolute inset-0 pointer-events-none" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="*" element={<Login />} />
          </Routes>
        </div>
      </Router>
    );
  }

  if (hasSession && !currentUser) {
    return (
      <Router>
        <div className="min-h-screen bg-gray-100 dark:bg-gray-950 flex items-center justify-center relative overflow-hidden">
          <LoadingIndicator label="Preparando tu sesión..." />
        </div>
      </Router>
    );
  }

  const handleDeleteSupplier = async (supplierId) => {
    try {
      await dbDeleteSupplier?.(supplierId);
      // Also remove supplierId references from transactions (optional: mark supplierId null)
      setTransactions(prev => prev.map(t => t.supplierId === supplierId ? { ...t, supplierId: null } : t));
      push('Proveedor eliminado', { type: 'warning' });
    } catch (e) {
      console.error('Delete supplier failed', e);
      push(e?.message || 'No se pudo eliminar el proveedor', { type: 'error' });
    }
  };

  // Actualizar cliente existente
  const handleUpdateClient = async (updatedClient) => {
    try {
      await dbUpdateClient?.(updatedClient.id, updatedClient);
      push('Cliente actualizado', { type: 'success' });
    } catch (e) {
      console.error('Update client failed', e);
      push(e?.message || 'No se pudo actualizar el cliente', { type: 'error' });
    }
  };

  // Función para añadir una nueva transacción
  const handleAddTransaction = async (newTrans) => {
    // Normalize and fill sensible defaults so downstream components (filters/views)
    // will include the transaction immediately (e.g. branch-based filtering, deposits)
    const originBranch = newTrans.branchId || currentUser?.branchId || null;
    const normalizedTarget = newTrans.type === 'transfer'
      ? (newTrans.targetBranchId != null && newTrans.targetBranchId !== '' ? newTrans.targetBranchId : null)
      : (newTrans.targetBranchId ?? null);

    const tx = {
      ...newTrans,
      id: newTrans.id || Date.now(),
      branchId: originBranch,
      ...(newTrans.type === 'transfer' ? {
        sourceBranchId: newTrans.sourceBranchId != null && newTrans.sourceBranchId !== ''
          ? newTrans.sourceBranchId
          : originBranch,
        targetBranchId: normalizedTarget,
      } : {}),
      deposits: Array.isArray(newTrans.deposits) ? newTrans.deposits : [],
      depositAmount: newTrans.depositAmount != null ? Number(newTrans.depositAmount) : (Array.isArray(newTrans.deposits) ? newTrans.deposits.reduce((s,d)=>s+Number(d.amount||0),0) : 0),
      // Status pipeline: sales start as pending_deposit (until any deposit is recorded),
      // then pending_delivery once there are deposits, and finally delivered when fully delivered.
      status: (() => {
        if (newTrans.type === 'sale') {
          return (Array.isArray(newTrans.deposits) && newTrans.deposits.length > 0) ? 'pending_delivery' : 'pending_deposit';
        } else if (newTrans.type === 'order') {
          return newTrans.status || 'pending_deposit';
        } else if (newTrans.type === 'transfer') {
          return 'delivered'; // Transfers are immediate
        }
        return 'pending_deposit';
      })(),
      history: newTrans.history || [{ ts: new Date().toISOString(), event: 'created' }],
      deliveredAmount: newTrans.deliveredAmount || null
    };

    // If transaction includes an immediate broker commission, credit it to the broker's balance now
    if (tx.brokerId && tx.brokerCommission && tx.brokerCommissionPaid && !tx.brokerCommissionRecorded) {
      const commissionValue = Number(tx.brokerCommission || 0);
      if (commissionValue > 0) {
        setBrokers(prev => prev.map(b => b.id === tx.brokerId ? { ...b, balance: (b.balance || 0) + commissionValue } : b));
        // record accounting movement in tx.history
        tx.history = [...(tx.history || []), { ts: new Date().toISOString(), event: 'broker_commission_paid', note: `Se abonó comisión de ${commissionValue.toLocaleString('es-ES')} al broker ${tx.brokerId}` }];
        tx.brokerCommissionRecorded = true;
      }
    }

    // Persist via DataProvider if available
    if (typeof useData === 'function') {
      try {
        await dbAddTransaction(tx);
        push('Operación creada', { type: 'success' });
      } catch (e) {
        console.error('Add transaction failed', e);
        const details = Array.isArray(e?.cause) ? e.cause.join('. ') : (e?.message || 'No se pudo crear la operación');
        push(details, { type: 'error' });
        throw e;
      }
    } else {
      setTransactions(prevTransactions => [...prevTransactions, tx]);
    }
  };

  // Función para actualizar depósitos de una transacción de tipo 'order'
  const handleUpdateTransactionDeposits = async (transactionId, newDeposits) => {
    console.debug('[App] handleUpdateTransactionDeposits', transactionId, newDeposits && newDeposits.length);
    // Local state update and status advancement
    setTransactions(prevTransactions => 
      prevTransactions.map(trans => {
        if (trans.id !== transactionId) return trans;
        const totalDeposited = (newDeposits || []).reduce((s, d) => s + (Number(d.amount || 0) || 0), 0);
        // If the transaction doesn't have a depositAccount but is linked to a supplier, try to set it
        let depositAccount = trans.depositAccount;
        if ((!depositAccount || depositAccount === '') && trans.supplierId) {
          const supplier = suppliers.find(s => s.id === trans.supplierId) || null;
          if (supplier && supplier.accountName) depositAccount = supplier.accountName;
        }
        // If this is a sale and we've recorded deposits, move it to pending_delivery
        const newStatus = (trans.type === 'sale') ? ((totalDeposited > 0) ? 'pending_delivery' : 'pending_deposit') : trans.status;
        return { ...trans, deposits: newDeposits, depositAmount: totalDeposited, depositAccount, status: newStatus };
      })
    );

    // Persist changes on server: insert new deposits, delete removed ones
    try {
      const current = transactions.find(t => t.id === transactionId);
      const existingIds = new Set((newDeposits || []).filter(d => d.id).map(d => d.id));
      // Delete deposits that were removed (pass full object so provider can delete storage object too)
      if (Array.isArray(current?.deposits)) {
        for (const d of current.deposits) {
          if (d.id && !existingIds.has(d.id)) {
            await dbDeleteDeposit?.(d);
          }
        }
      }
      // Insert new deposits (those without id). Upload proof files first when present.
      for (const d of newDeposits || []) {
        if (!d.id) {
          let proofMeta = null;
          try {
            const file = d.proofFile || (typeof d.proof === 'object' && d.proof && d.proof.name ? d.proof : null);
            if (file) {
              const { path, url } = await uploadDepositProof?.(file, { transactionId });
              if (path || url) {
                proofMeta = {
                  proof_url: url || null,
                  proof_path: path || null,
                  proof_name: file.name || null,
                  proof_type: file.type || null,
                  proof_size: file.size ?? null,
                };
              }
            }
          } catch (e) {
            console.warn('Upload proof failed (non-fatal):', e?.message || e);
          }
          await dbAddDeposit({
            transaction_id: transactionId,
            amount: d.amount,
            reference: d.reference || null,
            account_name: (transactions.find(t => t.id === transactionId)?.depositAccount) || null,
            deposit_date: d.date || null,
            metadata: proofMeta || undefined,
          });
        }
      }
      // After persisting, refresh the deposits from DB to pick up real IDs and recompute totals
      try {
        const { data: freshDeposits } = await dbListDeposits?.(transactionId) || { data: null };
        if (Array.isArray(freshDeposits)) {
          const totalDeposited = freshDeposits.reduce((s, d) => s + (Number(d.amount || 0) || 0), 0);
          setTransactions(prev => prev.map(t => t.id === transactionId ? { ...t, deposits: freshDeposits, depositAmount: totalDeposited } : t));
        }
      } catch (e) {
        console.warn('Refresh deposits after insert failed (non-fatal)', e);
      }
    } catch (e) {
      console.error('Persist deposits failed', e);
    }
  };

  // Función para marcar pedido como entregado
  // Handle delivery for orders or sales. Signature flexible:
  // handleMarkAsDelivered(transactionId, proofFile)  => old behavior (orders)
  // handleMarkAsDelivered(transactionId, deliveredAmount, proofFile) => for sales with actual received amount
  const handleMarkAsDelivered = async (transactionId, arg2, arg3) => {
    setTransactions(prevTransactions => 
      prevTransactions.map(trans => {
        if (trans.id !== transactionId) return trans;

        // Determine arguments
        let deliveredAmount = null;
        let proofFile = null;
        if (arg3 !== undefined) {
          deliveredAmount = parseFloat(arg2 || 0);
          proofFile = arg3;
        } else {
          proofFile = arg2;
        }

        const now = new Date().toISOString();

        // Compute new deliveredAmount by accumulating partial deliveries
        const previousDelivered = parseFloat(trans.deliveredAmount || 0);
        const newDelivered = deliveredAmount !== null ? previousDelivered + deliveredAmount : previousDelivered;

        // Append history entry
        const historyEntry = {
          ts: now,
          event: 'delivered',
          note: deliveredAmount !== null ? `Entrega parcial: +${deliveredAmount} (total ${newDelivered})` : 'Entrega confirmada',
          proof: proofFile ? (proofFile.name || proofFile) : null
        };

        // Compute expected thresholds for status transitions (fee-on-top model)
        const base = trans.amount != null ? Number(trans.amount) : 0;
        const salePct = trans.salePercentage != null ? Number(trans.salePercentage) : 0;
        const ourFee = trans.type === 'sale' ? Math.round((base * salePct / 100) * 100) / 100 : 0;
        const brokerPct = trans.brokerPercentage != null ? Number(trans.brokerPercentage) : 0;
        const brokerFee = trans.type === 'sale' && brokerPct > 0 ? Math.round((base * brokerPct / 100) * 100) / 100 : 0;
        let providerFee = 0;
        if (trans.type === 'sale' && trans.supplierId) {
          const supplier = suppliers.find(s => String(s.id) === String(trans.supplierId));
          const feeRate = supplier?.feeRate != null ? Number(supplier.feeRate) : null;
          if (feeRate && !Number.isNaN(feeRate)) providerFee = Math.round((base * feeRate / 100) * 100) / 100;
        }
        const expectedTotal = trans.type === 'sale' ? (base + ourFee + brokerFee + providerFee) : base;

        let updated = {
          ...trans,
          // If we've reached or exceeded expected total, mark delivered; otherwise set to pending_delivery for sales
          status: (newDelivered >= expectedTotal) ? 'delivered' : (trans.type === 'sale' ? 'pending_delivery' : trans.status),
          deliveryProof: proofFile ? (proofFile.name || proofFile) : null,
          deliveredDate: now,
          history: [...(trans.history || []), historyEntry],
          deliveredAmount: newDelivered
        };

        // Update depositAmount to reflect total received if it's a sale
        if (trans.type === 'sale') {
          updated.depositAmount = newDelivered || trans.depositAmount;
        }

        // If there are deposit entries for this transaction, consume them FIFO by allocating usedAmount
        if (trans.deposits && trans.deposits.length && deliveredAmount !== null && deliveredAmount > 0) {
          let remainingToAllocate = deliveredAmount;
          const newDeposits = (trans.deposits || []).map(d => ({ ...d }));
          for (let i = 0; i < newDeposits.length && remainingToAllocate > 0; i++) {
            const d = newDeposits[i];
            const available = Number(d.amount || 0) - Number(d.usedAmount || 0);
            if (available <= 0) continue;
            const allocate = Math.min(available, remainingToAllocate);
            d.usedAmount = (Number(d.usedAmount || 0) + allocate);
            remainingToAllocate -= allocate;
          }
          updated.deposits = newDeposits;
          // Recompute depositAmount as sum of deposits
          updated.depositAmount = (newDeposits || []).reduce((s, d) => s + (Number(d.amount || 0) || 0), 0);
        }

        return updated;
      })
    );

  // Update client and supplier balances based on the delta of this delivery
    if (arg3 !== undefined) {
      const deliveredAmount = parseFloat(arg2 || 0);

      // Update clients and suppliers using functional sets to get latest state
      setTransactions(prevTransactions => {
        const trans = prevTransactions.find(t => t.id === transactionId);
        if (!trans) return prevTransactions;

        if (trans.type === 'sale') {
          const expected = parseFloat(trans.salePrice || 0);
          // We compute delta as the incremental deliveredAmount (arg2) minus 0 relative to expected? For partials, the client balance should change by the incremental amount minus proportional expected?
          // Simpler: add the incremental deliveredAmount to supplier balance; adjust client balance only when delivered exceeds expected (overpayment) or negative when underpaid.
          const prevDelivered = parseFloat(trans.deliveredAmount || 0) - deliveredAmount; // previous before this increment
          const prevReceived = prevDelivered || trans.depositAmount || 0;
          const newReceived = prevReceived + deliveredAmount;
          const deltaClient = (newReceived - expected);

          if (trans.clientId) {
            setClients(prevClients => prevClients.map(c => c.id === trans.clientId ? { ...c, balance: (c.balance || 0) + deltaClient } : c));
          }
        }

        // For supplier, add the incremental deliveredAmount to supplier balance if supplier exists
        if (trans.supplierId) {
          const supplier = suppliers.find(s => s.id === trans.supplierId);
          if (supplier) {
            setSuppliers(prevSuppliers => prevSuppliers.map(s => s.id === supplier.id ? { ...s, balance: (s.balance || 0) + deliveredAmount } : s));
          }
        }

        return prevTransactions;
      });
    }
    // Persist delivery in DB if helpers available and delivered amount provided
    try {
      const hasAmount = arg3 !== undefined;
      if (hasAmount) {
        if (typeof dbDeliver === 'function') {
          const deliveredAmount = parseFloat(arg2 || 0);
          await dbDeliver(transactionId, deliveredAmount, arg3);
        }
      } else if (typeof dbMarkDelivered === 'function') {
        await dbMarkDelivered(transactionId);
      }
    } catch (e) {
      console.error('Persist delivery failed', e);
    }
  };

  // Nueva función para cancelar pedido
  const handleCancelOrder = async (transactionId) => {
    setTransactions(prevTransactions => 
      prevTransactions.map(trans => 
        trans.id === transactionId 
          ? { ...trans, status: 'cancelled', cancelledDate: new Date().toISOString().split('T')[0] }
          : trans
      )
    );
    try {
      if (typeof dbCancelTransaction === 'function') {
        await dbCancelTransaction(transactionId);
      }
    } catch (e) {
      console.error('Persist cancel failed', e);
    }
  };

  // Función para actualizar proveedores
  const handleUpdateSupplier = async (updatedSupplier) => {
    try {
      await dbUpdateSupplier?.(updatedSupplier.id, updatedSupplier);
      push('Proveedor actualizado', { type: 'success' });
    } catch (e) {
      console.error('Update supplier failed', e);
      push(e?.message || 'No se pudo actualizar el proveedor', { type: 'error' });
    }
  };

  // Funciones para añadir clientes, proveedores y brokers desde UI
  const handleAddClient = async (client) => {
    console.log('[App] handleAddClient called with:', client);
    try {
      await dbAddClient?.({ ...client, balance: client.balance ?? 0 });
      push('Cliente agregado', { type: 'success' });
    } catch (e) {
      console.error('[App] Add client failed', e);
      push(e?.message || 'No se pudo agregar el cliente', { type: 'error' });
    }
  };

  const handleDeleteClient = async (clientId) => {
    try {
      await dbDeleteClient?.(clientId);
      setTransactions(prev => prev.map(t => t.clientId === clientId ? { ...t, clientId: null } : t));
      push('Cliente eliminado', { type: 'warning' });
    } catch (e) {
      console.error('Delete client failed', e);
      push(e?.message || 'No se pudo eliminar el cliente', { type: 'error' });
    }
  };

  const handleAddSupplier = async (supplier) => {
    try {
      await dbAddSupplier?.({ ...supplier, balance: supplier.balance ?? 0 });
      push('Proveedor agregado', { type: 'success' });
    } catch (e) {
      console.error('Add supplier failed', e);
      push(e?.message || 'No se pudo agregar el proveedor', { type: 'error' });
    }
  };

  const handleAddBroker = async (broker) => {
    try {
      await dbAddBroker?.({ ...broker, balance: broker.balance ?? 0, commissionRate: broker.commissionRate ?? 0 });
      push('Broker agregado', { type: 'success' });
    } catch (e) {
      console.error('Add broker failed', e);
      push(e?.message || 'No se pudo agregar el broker', { type: 'error' });
    }
  };

  const handleAddBranch = async (branch) => {
    try {
      await dbAddBranch?.({ ...branch, cashBalance: Number(branch.cashBalance || 0) });
      push('Sucursal agregada', { type: 'success' });
    } catch (e) {
      console.error('Add branch failed', e);
      push(e?.message || 'No se pudo agregar la sucursal', { type: 'error' });
    }
  };

  const handleUpdateBranch = async (updatedBranch) => {
    try {
      await dbUpdateBranch?.(updatedBranch.id, updatedBranch);
      push('Sucursal actualizada', { type: 'success' });
    } catch (e) {
      console.error('Update branch failed', e);
      push(e?.message || 'No se pudo actualizar la sucursal', { type: 'error' });
    }
  };

  // Función para editar/actualizar una transacción existente
  const handleEditTransaction = async (updatedTrans) => {
    console.debug('[App] handleEditTransaction', updatedTrans.id, updatedTrans.depositAccount);
    try {
      await dbUpdateTransaction?.(updatedTrans.id, updatedTrans);
      push('Operación actualizada', { type: 'success' });
    } catch (e) {
      console.error('Update transaction failed', e);
      const msg = Array.isArray(e?.cause) ? e.cause.join('. ') : (e?.message || 'No se pudo actualizar la operación');
      push(msg, { type: 'error' });
      throw e;
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    console.groupCollapsed('[App] handleDeleteTransaction');
    console.info('transactionId:', transactionId, 'useDb:', useDb);
    // Optimistic UI: remove immediately and rollback on error
    let snapshot = null;
    setTransactions(prev => {
      snapshot = prev;
      return prev.filter(t => String(t.id) !== String(transactionId));
    });
    try {
      if (useDb) {
        console.info('[App] attempting HARD delete in DB mode');
        await dbDeleteTransaction?.(transactionId);
        console.info('[App] delete success (db)');
        push('Operación eliminada', { type: 'warning' });
      } else {
        await dbDeleteTransaction?.(transactionId);
        console.info('[App] delete success (mock)');
        push('Operación eliminada', { type: 'warning' });
      }
    } catch (e) {
      console.error('[App] delete failed', {
        message: e?.message,
        name: e?.name,
        status: e?.status,
        code: e?.code,
        details: e?.details,
        hint: e?.hint,
      });
      // Fallback: intentar cancelar si la eliminación no está permitida por políticas
      try {
        console.info('[App] trying cancel fallback');
        await dbCancelTransaction?.(transactionId);
        console.info('[App] cancel fallback success');
        push('No se pudo eliminar por políticas. Operación cancelada.', { type: 'warning' });
      } catch (e2) {
        console.error('[App] cancel fallback failed', {
          message: e2?.message,
          name: e2?.name,
          status: e2?.status,
          code: e2?.code,
          details: e2?.details,
          hint: e2?.hint,
        });
        // Mantener eliminación optimista en UI para no bloquear flujo
        push('No se pudo eliminar la operación (se ocultó localmente).', { type: 'error' });
      }
    }
    console.groupEnd();
  };

  const handleToggleCommission = async (transactionId, paid) => {
    try {
      await dbMarkBrokerCommissionPaid?.(transactionId, paid);
      push(paid ? 'Comisión marcada como pagada' : 'Comisión marcada como pendiente', { type: 'success' });
    } catch (e) {
      console.error('Toggle commission failed', e);
      push(e?.message || 'No se pudo actualizar la comisión', { type: 'error' });
      throw e;
    }
  };

  // Calcular fondos propios comprometidos
  const committedOwnFunds = transactions.reduce((sum, trans) => {
    if (trans.type === 'order' && trans.fundSource === 'own' && trans.status === 'pending') {
      return sum + trans.amount;
    }
    return sum;
  }, 0);

  // Filtrar transacciones según el usuario
  const getUserTransactions = () => {
    const isAdmin = currentUser?.role === 'admin';
    const branchId = currentUser?.branchId;
    if (isAdmin || !branchId) return transactions;
    return transactions.filter(t => String(t.branchId) === String(branchId));
  };

  function Topbar({ user }) {
    const location = useLocation();
    const navigate = useNavigate();
    const path = location?.pathname || '/';
    const pageMap = {
      '/': 'Panel',
      '/transactions': 'Operaciones',
      '/clients': 'Clientes',
      '/suppliers': 'Proveedores',
      '/brokers': 'Brokers',
      '/branches': 'Sucursales',
      '/purchase-management': 'Gestión Compras',
      '/pending-cash-calendar': 'Efectivo Pendiente',
      '/profits': 'Ganancias',
      '/reports': 'Reportes',
      '/settings': 'Configuración',
      '/status': 'Estado',
      '/login': 'Login',
      '/reset': 'Reset',
    };
    const page = pageMap[path] || (path?.split('/')?.filter(Boolean)?.slice(-1)?.[0] || '').replace(/-/g,' ') || 'Página';
    const userLabel = user?.name || user?.email || 'usuario';
    return (
      <div className="sticky top-0 z-40 w-full border-b border-gray-200 bg-white/80 backdrop-blur dark:border-gray-800 dark:bg-gray-900/80">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="font-semibold text-gray-700 dark:text-gray-200 hover:underline">MONIPAY</Link>
            <span className="text-gray-300 dark:text-gray-600">/</span>
            <span className="text-gray-500 dark:text-gray-400">{userLabel}</span>
            <span className="text-gray-300 dark:text-gray-600">/</span>
            <span className="text-gray-700 dark:text-gray-200 font-medium">{page}</span>
          </div>
          <div className="flex items-center gap-2">
            <BotNotifications />
            <ThemeToggle />
            <Link
              to="/settings"
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              title="Configuración"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Config</span>
            </Link>
            <button
              type="button"
              onClick={async () => { await appLogout('/login', { navigate, replace: true }); }}
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Backdrop that hides the global spotlight on routes that provide their own effect (none currently)
  function RouteAwareBackdrop() {
    const location = useLocation();
    const path = location?.pathname || '/';
    const showBento = true;
    return (
      <>
        {showBento && <BentoGrid variant="bold" className="fixed inset-0 pointer-events-none" />}
      </>
    );
  }

  return (
    <Router>
  <div className="flex h-screen overflow-hidden bg-gray-100 dark:bg-gray-950 relative">
          {/* Magic Bento Grid effect for the entire app (hide global spotlight on /blank) */}
          <RouteAwareBackdrop />
          
          <Sidebar
            currentUser={currentUser}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
          />
          <div className="flex-1 overflow-auto bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur-sm relative z-[1]">
            <LiveRegion />
            <CommandPalette />
            {/* Global Topbar (with breadcrumbs) */}
            <Topbar user={currentUser} />
            {dataError && (
              <div className="m-3 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm relative z-10">
                Problema cargando datos desde la base. Mostrando datos en memoria. <span className="opacity-70">(Detalle en consola)</span>
              </div>
            )}
            <div className="px-4 pb-10 pt-4">
            <Routes>
            <Route path="/" element={<Dashboard transactions={getUserTransactions()} branches={branches} currentUser={currentUser} />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/clients" element={<Clients clients={clients} transactions={getUserTransactions()} onAddClient={handleAddClient} onDeleteClient={handleDeleteClient} onUpdateClient={handleUpdateClient} />} />
            <Route path="/suppliers" element={<Suppliers suppliers={suppliers} transactions={getUserTransactions()} onUpdateSupplier={handleUpdateSupplier} onAddSupplier={handleAddSupplier} onDeleteSupplier={handleDeleteSupplier} />} />
            <Route path="/brokers" element={<Brokers brokers={brokers} transactions={getUserTransactions()} onAddBroker={handleAddBroker} onUpdateBroker={dbUpdateBroker} />} />
            <Route path="/branches" element={<Branches branches={branches} transactions={getUserTransactions()} currentUser={currentUser} onAddBranch={handleAddBranch} onUpdateBranch={handleUpdateBranch} onTransferBetweenBranches={dbTransferBranchCash} />} />
            <Route path="/businesses" element={<Businesses />} />
            <Route 
              path="/transactions" 
              element={
                <Transactions 
                  transactions={getUserTransactions()} 
                  clients={clients} 
                  suppliers={suppliers} 
                  brokers={brokers} 
                  branches={branches} 
                  isLoading={loading}
                  onAddTransaction={handleAddTransaction}
                  onEditTransaction={handleEditTransaction}
                  onDeleteTransaction={handleDeleteTransaction}
                  onUpdateTransactionDeposits={handleUpdateTransactionDeposits}
                  onMarkAsDelivered={handleMarkAsDelivered}
                  onToggleCommission={handleToggleCommission}
                  onCancelOrder={handleCancelOrder} // Pasar función de cancelar
                  committedOwnFunds={committedOwnFunds}
                  ownFundsLimit={OWN_FUNDS_LIMIT}
                />
              } 
            />
            <Route 
              path="/purchase-management" 
              element={
                <PurchaseManagement 
                  transactions={getUserTransactions()} 
                  suppliers={suppliers} 
                  onUpdateTransactionDeposits={handleUpdateTransactionDeposits}
                  onMarkAsDelivered={handleMarkAsDelivered}
                />
              } 
            />
            <Route 
              path="/pending-cash-calendar" 
              element={
                <PendingCashCalendar 
                  transactions={getUserTransactions()} 
                  suppliers={suppliers}
                />
              } 
            />
            <Route path="/reports" element={<Reports transactions={getUserTransactions()} branches={branches} brokers={brokers} clients={clients} />} />
            <Route path="/profits" element={<Profits />} />
            <Route path="/settings" element={<SettingsPage useGlobalHeader />} />
            <Route path="/status" element={<StatusPage />} />
            
            </Routes>
            </div>
          </div>
        </div>
    </Router>
  );
}

export default App;
